import React, { useEffect, useRef, useState } from "react";
import css from "./AiChatBot.module.css";
import { communityPostAccess, PRIMARY_GPT_NAME, PRIMARY_RULEBOOK_GROUP_ID } from "../../util/types";
import { FormattedMessage } from "react-intl";
import ChatBotSideBar from "../../containers/AiChatBotPage/ChatBotSideBar/ChatBotSideBar";
import IconCollection from "../IconCollection/IconCollection";
import Button from "../Button/Button";
import classNames from "classnames";
import { capitalizeWords, currentUserRole, getUserPermissions } from "../../util/data";
import SideBar from "./FeedTabs/SideBar/SideBar";
import MobileBottomTabs from './MobileBottomTabs/MobileBottomTabs';
import {
  EXPLORE,
  NOTIFICATION,
} from '../../util/types';
import NotificationMobileModal from "../NotificationDropdown/NotificationMobileModal";
import CreatePostBar from "../../containers/CircleCommunityPage/components/CreatePostBar/CreatePostBar";
import { createResourceLocatorString } from "../../util/routes";
import { useHistory } from "react-router-dom";
import { useRouteConfiguration } from "../../context/routeConfigurationContext";
import { v4 as uuidv4 } from 'uuid';

const AiChatBot = (props) => {
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const {
    fetchChatInProgress,
    chatDetails,
    fetchChatError,
    gptsDetails = [],
    currentUser,
    onUserChatWithGpt,
    userChatInProgress,
    userChatError,
    params,
    userFavChatDetails,
    scrollToSection,
    onToggleCreatePost,
    onFetchNotifications,
    requestNotificationsInProgress,
    notificationsData,
    requestNotificationPreferenceInProgress,
    notificaitonPreferenceData,
    onMarkNotificationRead,
    onManageDisableScrolling,
    onGetNotificationPreference,
    onUpdateNotificationPreference,
    onMarkAllNotificationsAsRead,
    isCreatePost,
    requestDirectUpload,
    currentCommunityMember,
    spaceListData,
    advancedSearchData,
    onSubscriptionPermissionRequest,
    onAdvancedSearch,
    userChatDetails,
    onAddOrRemoveFavChats,
  } = props;

  const chatBoxRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isSideBarOpen, setIsSideBarOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const userSubscriptionRole = currentUserRole(currentUser);

  const checkCreatePostAuth = getUserPermissions(userSubscriptionRole, communityPostAccess);

  const { favGpt = [], gpts = [] } = userFavChatDetails || {};
  const favFileIds = favGpt?.map((chat) => chat.rulebook_id) || [];

  useEffect(() => {
    setMessages([])
  }, [params]);


  const handleIconClick = content => {
    const { tab } = content;

    if (tab === 'MENU') {
      setIsSideBarOpen(tab);
    } else if (tab === 'ADD_MENU') {
      if (!checkCreatePostAuth) {
        onSubscriptionPermissionRequest(true);
        return;
      }
      onToggleCreatePost(true);
    }
    else if (tab === EXPLORE) {
      window.location.href = '/feed-page/group-search';
    } else if (tab === NOTIFICATION) {
      setIsOpen(true);
    }
  };

  const closeSideBar = () => {
    setIsSideBarOpen(false);
  };


  const { rulebookId, sessionId } = params || {};

  const selectedRulebook = gptsDetails?.find((e) => e.rulebook_id == rulebookId) || {};
  const { group_id, rulebook_id, rulebook_name, logo_url } = selectedRulebook || {};
  const selectedChat = chatDetails?.filter(e => e.session_id == sessionId) || {};

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages, userChatInProgress]); // Scroll when these values change


  const sendMessage = async (input) => {
    if (!input.trim()) return;

    const userMessage = { text: input, sender: "user" };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");

    onUserChatWithGpt({
      rulebook_id: (rulebook_id && rulebookId !== PRIMARY_RULEBOOK_GROUP_ID) ? rulebook_id : "general",
      user_id: currentUser?.id?.uuid,
      query: input,
      group_id: group_id ? group_id : PRIMARY_RULEBOOK_GROUP_ID,
      session_id: sessionId ? sessionId : uuidv4()
    })
      .then((response) => {
        setMessages((prev) => [...prev, { text: response?.response || response, sender: "bot" }]);
      })
      .catch((error) => {
        setMessages((prev) => [...prev, { text: "Error fetching response. Try again.", sender: "bot" }]);
      });
  };

  const formatMessage = (message) => {
    if (typeof message !== "string") return message;

    // ✅ Split text into lines & map each one
    return message.split("\n").map((line, index) => {
      // ✅ Convert bullet points & numbered lists into `<li>` elements
      if (line.match(/^\d+\./) || line.match(/^[-*•]/)) {
        return <li key={index}>{line.replace(/^[-*•]\s*/, "").trim()}</li>;
      }

      // ✅ Convert headings (if they end with `:` or start with `#`)
      if (line.trim().endsWith(":") || line.startsWith("#")) {
        return <strong key={index} className={css.heading}>{line.trim()}</strong>;
      }

      // ✅ Otherwise, treat it as a normal paragraph
      return <p key={index} className={css.paragraph}>{line.trim()}</p>;
    });
  };

  const rulebookName = capitalizeWords(rulebook_name) || PRIMARY_GPT_NAME;

  const mobileBottomTabsComponent = <MobileBottomTabs handleIconClick={handleIconClick} />;

  const NotificationMobileComponent = (
    <NotificationMobileModal
      onFetchNotifications={onFetchNotifications}
      requestNotificationsInProgress={requestNotificationsInProgress}
      notificationsData={notificationsData}
      isOpen={isOpen}
      setIsOpen={() => setIsOpen(false)}
      onMarkNotificationRead={onMarkNotificationRead}
      onManageDisableScrolling={onManageDisableScrolling}
      requestNotificationPreferenceInProgress={requestNotificationPreferenceInProgress}
      notificaitonPreferenceData={notificaitonPreferenceData}
      onGetNotificationPreference={onGetNotificationPreference}
      onUpdateNotificationPreference={onUpdateNotificationPreference}
      onMarkAllNotificationsAsRead={onMarkAllNotificationsAsRead}
    />
  );

  const handlePostCreation = async (apiParams, spaceId, postId) => {
    const response = await onCreateSpacePost(apiParams, spaceId, postId)
    if (response?.id) {
      return history.push(
        createResourceLocatorString(
          'PostViewPage',
          routeConfiguration,
          { spaceId, slug: response?.slug, postId: response?.id },
        )
      );
    }
  };

  return (
    <div className={css.container}>
      {/* Sidebar */}
      <div className={classNames(css.sidebar, isSideBarOpen && css.sidebarOpen)}>

        <ChatBotSideBar
          currentPage={'AiChatBotPage'}
          gptsDetails={gptsDetails}
          fetchChatInProgress={fetchChatInProgress}
          chatDetails={chatDetails}
          fetchChatError={fetchChatError}
          isSideBarOpen={isSideBarOpen}
          userFavChatDetails={userFavChatDetails}
          scrollToSection={scrollToSection}
          params={params}
          userChatDetails={userChatDetails}
          currentUser={currentUser}
        />
      </div>

      {/* Chat Section */}
      <div className={css.chatSection}>

        <div className={css.chatSectionHeader}>
          {logo_url ? <img src={logo_url} alt="image not found" width="126px"
            height="126px" /> : <IconCollection name="thqAI" />}
          <h2 className={css.title}>{rulebookName}</h2>
          <div className={css.favouriteButton} onClick={() => {
            typeof onAddOrRemoveFavChats == "function" && currentUser?.id?.uuid && onAddOrRemoveFavChats({ rulebook_id: rulebook_id, user_id: currentUser?.id?.uuid })
          }}>
            {favFileIds.includes(rulebook_id) ? <IconCollection name="myFavouriteIconFill" /> : <IconCollection name="myFavouriteIconFillBorder" />}
          </div>
          {/* <p className={css.description}>
            <FormattedMessage id="AiChatBotPage.heading" values={{ rulebook_name: rulebookName }} />
          </p> */}

          {/* Suggested Questions dummy will update later */}
          {/* <div className={css.suggestions}>
            <Button onClick={() => {
              sendMessage('What are the registration requirements for horses?')
            }}><FormattedMessage id="AiChatBotPage.text1" /></Button>
            <Button onClick={() => {
              sendMessage('How do I apply for registration in horses community?')
            }}><FormattedMessage id="AiChatBotPage.text2" /></Button>
            <Button onClick={() => {
              sendMessage('What penalties exist in horses rulebook ?')
            }} ><FormattedMessage id="AiChatBotPage.text3" /></Button>
            <Button onClick={() => {
              sendMessage('What are the types of horses ?')
            }} ><FormattedMessage id="AiChatBotPage.text4" /></Button>
          </div> */}
          {userChatError ? <FormattedMessage id="AiChatBotPage.errorText" /> : null}
          {/* Chat Box */}

          {(messages.length > 0 || userChatInProgress || (selectedChat && selectedChat.length > 0)) ? <div className={css.chatBox} ref={chatBoxRef}>

            {(selectedChat && selectedChat.length > 0) ? selectedChat.map((chat, index) => {
              return (
                <>
                  <span className={css.userMessage}>{formatMessage(chat.query)} </span>
                  <span className={css.botMessage}>{formatMessage(chat.response)}</span>
                </>
              )
            }) : null}

            {messages?.map((msg, index) => (
              <div key={index} className={msg.sender === "user" ? css.userMessage : css.botMessage}>
                {formatMessage(msg.text)}
              </div>
            ))}
            {userChatInProgress ? <div className={css.loading}><FormattedMessage id="AiChatBotPage.thinking" /></div> : null}
          </div> : null}

          {/* Chat Input */}
          <div className={css.inputContainer}>
            <textarea
              type="textarea"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className={css.chatInput}
              placeholder={`Message ${rulebookName}`}
            />
            <Button className={css.plusButton}>
              {/* <IconCollection name="iconAdd" /> */}
            </Button>
            <Button onClick={() => sendMessage(input)} className={css.sendButton} disabled={userChatInProgress}>
              <IconCollection name="send_message" />
              Send message
            </Button>
          </div>
          <div className={css.bottomInfo}>
            <FormattedMessage id="AiChatBot.bottomInfo" />
          </div>
        </div>
      </div>
      {mobileBottomTabsComponent}
      <SideBar
        isOpen={isSideBarOpen}
        onClose={closeSideBar}
        currentPage={'AiChatBotPage'}
        gptsDetails={gptsDetails}
        fetchChatInProgress={fetchChatInProgress}
        chatDetails={chatDetails}
        fetchChatError={fetchChatError}
        isSideBarOpen={isSideBarOpen}
        userFavChatDetails={userFavChatDetails}
        scrollToSection={scrollToSection}
        currentUser={currentUser}
      />
      {NotificationMobileComponent}

      {isCreatePost ? (
        <div>
          <CreatePostBar
            requestDirectUpload={requestDirectUpload}
            currentCommunityMember={currentCommunityMember}
            spaceListData={spaceListData}
            onCreateSpacePost={handlePostCreation}
            isCreatePost={isCreatePost}
            onToggleCreatePost={value => {
              checkCreatePostAuth
                ? onToggleCreatePost(value)
                : onSubscriptionPermissionRequest(true);
            }}
            onAdvancedSearch={onAdvancedSearch}
            advancedSearchData={advancedSearchData}
            params={params}
          />
        </div>
      ) : null}

    </div>
  );
};

export default AiChatBot;
